import { QueryKeys, queryClient, useEntities } from "@/api/core";
import { statusSchema, type Status } from "@/schemas";
import { filteredArray } from "@/schemas/utils";

import fallbackStatuses from "../../../__fixtures__/fallback_landing_statuses.json";

/**
 * Seed the account and poll query caches from the embedded landing data so the
 * real <Status> component renders without firing a fetch per card (accounts)
 * or losing polls to an unauthenticated 401.
 */
function seedEmbeddedEntities(statuses: Status[]) {
  statuses.forEach((status) => {
    [status, status.reblog, status.quote].forEach((entity) => {
      if (!entity) return;

      queryClient.setQueryData(
        QueryKeys.accounts.account(entity.account.id),
        entity.account,
      );

      if (entity.poll) {
        queryClient.setQueryData(
          QueryKeys.polls.poll(entity.poll.id),
          entity.poll,
        );
      }
    });
  });
}

function useLandingStatuses() {
  const queryInfo = useEntities<Status>({
    queryKey: QueryKeys.statuses.landing(),
    endpoint: `/api/v1/landing/statuses`,
    schema: statusSchema,
    onSuccess: seedEmbeddedEntities,
  });

  let result = queryInfo.entities;

  if (queryInfo.isError || queryInfo.entities.length === 0) {
    const fallback = filteredArray(statusSchema).parse(fallbackStatuses);

    seedEmbeddedEntities(fallback);
    result = fallback;
  }

  return {
    ...queryInfo,
    entities: result,
  };
}

export { useLandingStatuses };
